import { Stack } from "@mui/material";
import { useEffect, useMemo } from "react";
import {
  useServiceRequestByOrganization,
} from "@/services/fhir/servicerequest/hooks/useServiceRequestByOrganization";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDict } from "@/utils/useDict";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildServiceRequestInsightCharts,
  buildServiceRequestPatientRows,
  SERVICE_REQUEST_STATUS_TRANSLATION,
} from "src-next/modules/clinical-insights/transformers/mapServiceRequestInsights";
import { useFhirInsightLoadPhase } from "src-next/modules/clinical-insights/hooks/useFhirInsightLoadPhase";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("service");

export function ServiceRequestAnalysis({
  count,
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
}: ClinicalInsightChildProps & { count?: number }) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const dict = useDict;

  const {
    data: serviceRequestData,
    loading: loadingServiceRequests,
    fetchServiceRequests,
  } = useServiceRequestByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    includeEncounter: true,
  });

  useEffect(() => {
    if (organizationId && isActive) fetchServiceRequests();
  }, [organizationId, dateRange?.startDate, dateRange?.endDate, isActive, fetchServiceRequests]);

  const { hasData } = useFhirInsightLoadPhase(
    Boolean(organizationId) && isActive,
    loadingServiceRequests,
    onLoadPhaseChange
  );

  const serviceRequestDataFiltered = useMemo(() => {
    if (!serviceRequestData?.serviceRequests) return serviceRequestData;
    if (practitionerFilter === "all") return serviceRequestData;
    const serviceRequests = serviceRequestData.serviceRequests.filter((sr) =>
      matchesPractitionerFilter(practitionerFilter, sr.requester?.reference)
    );
    return { ...serviceRequestData, serviceRequests };
  }, [serviceRequestData, practitionerFilter]);

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, rankingItems, total, filtered } = useMemo(
    () =>
      buildServiceRequestInsightCharts(serviceRequestDataFiltered ?? {}, {
        serviceRequestStatus: dict.serviceRequestStatus,
        serviceRequestPriority: dict.serviceRequestPriority,
      }),
    [serviceRequestDataFiltered, dict.serviceRequestStatus, dict.serviceRequestPriority]
  );

  const patientRows = useMemo(
    () =>
      buildServiceRequestPatientRows(
        filtered,
        serviceRequestDataFiltered?.patients || [],
        { serviceRequestStatus: dict.serviceRequestStatus }
      ),
    [filtered, serviceRequestDataFiltered?.patients, dict.serviceRequestStatus]
  );

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% dos ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const loadPhase = loadingServiceRequests ? "records" : "done";

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#ff9800"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={{ loaded: 0, total: 0 }}
        hasData={hasData || total > 0}
        isEmpty={total === 0 && !loadingServiceRequests}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => fetchServiceRequests()}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={SERVICE_REQUEST_STATUS_TRANSLATION}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
